import client from "@homer.core/client.info";
import session from "@homer.core/session";
import whenLoaded from "@homer.core/when.loaded";
import sort from "@homer.utility/object.sort";
import Uuid from "@homer.utility/uuid";
import isFunction from "@homer.utility/validator.isfunction";
import isObject from "@homer.utility/validator.isobject";
import isString from "@homer.utility/validator.isstring";
import isUndefined from "@homer.utility/validator.isundefined";
import filterUndefined from "@homer.utility/object.filter.undefined";
import bus from "./bus.js";
import custom from "./custom.js";
import validate from "./validate.js";

// Flags retain their signed 32-bit contract because they are processed bitwise.
const isInt32 = (value) => typeof value === "number" && value === (value | 0);

const flag = {
    isPageview: Math.pow(2, 1),
    isConversion: Math.pow(2, 2),
    isInteraction: Math.pow(2, 3),
};
const html = document.documentElement;
const head = document.head;
const useragent = navigator.userAgent || null;
const language = navigator.language || null;
const plugins = [...(globalThis.navigator?.plugins || [])]
    .map((plugin) => plugin.name)
    .sort((n, t) => (n > t ? 1 : n < t ? -1 : 0));
const mimetypes = [...(globalThis.navigator?.mimeTypes || [])].map((mimetype) => mimetype.type);
const pageUuid = Uuid.generate();

const getCurrency = () => {
    const node = head.querySelector('meta[itemprop="currency"]');

    return node ? node.getAttribute("content") : null;
};

export default class Event {
    #name;
    #data;

    constructor(name, details = {}, flags = 0, override = {}) {
        validate.fields([
            { name: "name", value: name, validator: { isString } },
            { name: "details", value: details, validator: { isObject } },
            { name: "flags", value: flags, validator: { isInteger: isInt32 } },
        ]);

        session.update(flags, flag);

        this.#name = name;
        this.#data = (() => {
            const promises = [
                +new Date(),
                whenLoaded,
                {
                    host: location.hostname,
                    locale: html.lang,
                    currency: getCurrency(),
                },
                {
                    uuid: pageUuid,
                    title: document.title,
                    url: location.href,
                    referrer: document.referrer || null,
                },
                client,
                session.current,
            ];
            const global = {};
            const local = {};

            for (let [key, value] of custom.entries()) {
                promises.push(
                    Promise.resolve(isFunction(value) ? value() : value).then((value) => {
                        if (!isUndefined(value)) {
                            global[key] = value;
                        }
                    }),
                );
            }

            for (let [key, value] of Object.entries(override)) {
                promises.push(
                    Promise.resolve(isFunction(value) ? value() : value).then((value) => {
                        if (!isUndefined(value)) {
                            local[key] = value;
                        }
                    }),
                );
            }

            return Promise.all(promises).then(([timestamp, loadtime, application, page, client, session]) => {
                return Object.freeze(
                    sort({
                        ...global,
                        ...local,
                        uuid: Uuid.generate(),
                        timestamp,
                        queued: 0,
                        ...Object.fromEntries(Object.keys(Event.flag).map((key) => [key, !!(flags & Event.flag[key])])),
                        application: {
                            ...application,
                            ...(global.application || {}),
                            ...(local.application || {}),
                        },
                        page: {
                            ...page,
                            loadtime: Math.round(loadtime),
                            ...(global.page || {}),
                            ...(local.page || {}),
                        },
                        client: { ...client, ...(global.client || {}), ...(local.client || {}) },
                        history: history.length,
                        navigator: {
                            useragent,
                            language,
                            plugins,
                            mimetypes,
                        },
                        session: { ...session, ...(global.session || {}), ...(local.session || {}) },
                        details: filterUndefined(details),
                    }),
                );
            });
        })();
    }

    toJSON() {
        return this.#data.then((data) => {
            return data;
        });
    }

    toString() {
        return this.#data.then((data) => {
            return JSON.stringify(data);
        });
    }

    dispatch() {
        return this.#data.then((data) => {
            bus.emit(this.#name, data);
        });
    }

    static flag = Object.freeze(flag);
    static validate = Object.freeze(validate);
}
